import React from 'react';
import ClanConnectLogo from '../../assets/images/clan_connect_logo.png';
import './addon-checkout.scss';

// Line icons (lucide shapes, same set the brand app uses).
const svgProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const GiftIcon = () => (
  <svg width='28' height='28' viewBox='0 0 24 24' {...svgProps}>
    <rect x='3' y='8' width='18' height='4' rx='1' />
    <path d='M12 8v13' />
    <path d='M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7' />
    <path d='M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5' />
  </svg>
);

const UsersIcon = () => (
  <svg width='28' height='28' viewBox='0 0 24 24' {...svgProps}>
    <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
    <circle cx='9' cy='7' r='4' />
    <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
    <path d='M16 3.13a4 4 0 0 1 0 7.75' />
  </svg>
);

const AlertIcon = () => (
  <svg width='28' height='28' viewBox='0 0 24 24' {...svgProps}>
    <circle cx='12' cy='12' r='10' />
    <path d='M12 8v4' />
    <path d='M12 16h.01' />
  </svg>
);

const CheckIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' {...svgProps} strokeWidth={2.5}>
    <path d='M20 6 9 17l-5-5' />
  </svg>
);

const LockIcon = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' {...svgProps}>
    <rect x='3' y='11' width='18' height='11' rx='2' />
    <path d='M7 11V7a5 5 0 0 1 10 0v4' />
  </svg>
);

/** The checkout pages have no site header (header.tsx hideHeader): the logo alone. */
export const CheckoutLogo: React.FC = () => (
  <img className='addon-checkout__logo' src={ClanConnectLogo} alt='ClanConnect' />
);

/** Error card (expired link, plan unavailable) in the same frame as the checkout. */
export const AddonCheckoutMessage: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <main className='addon-checkout'>
    <CheckoutLogo />
    <div className='addon-checkout__card'>
      <div className='addon-checkout__icon addon-checkout__icon--error'><AlertIcon /></div>
      <h1 className='addon-checkout__title'>{title}</h1>
      <p className='addon-checkout__body mb-0'>{message}</p>
    </div>
  </main>
);

interface Props {
  planName: string;
  brandName?: string;
  plan: any;          // signed plan row; null while loading
  error: string;
  loading: boolean;
  onPay: () => void;
}

/** One-time purchase opened from the brand app: one barter campaign or 20 proposal unlocks. */
export const AddonCheckout: React.FC<Props> = ({ planName, brandName, plan, error, loading, onPay }) => {
  if (error) {
    return <AddonCheckoutMessage title="Can't open this purchase" message={error} />;
  }

  const isBarterCampaign = planName === 'New Barter Campaign';
  const validDays = Number(plan?.days) || 30;
  const sym = String(plan?.currency || 'INR').toUpperCase() === 'INR' ? '₹' : `${plan?.currency} `;
  const price = plan ? `${sym}${Number(plan.amount).toLocaleString('en-IN')}` : '';

  const title = isBarterCampaign ? 'Publish your barter campaign' : 'Unlock 20 more proposals';
  const body = isBarterCampaign
    ? 'A one-time payment to publish one barter campaign on the free plan.'
    : 'A one-time payment to see more creator proposals on one of your barter campaigns.';
  const benefits = isBarterCampaign
    ? [
        'Publish 1 barter campaign',
        'Creators can send proposals for 30 days',
        'See up to 50 creator proposals',
        `Use it within ${validDays} days of buying`,
      ]
    : [
        'See 20 more creator proposals',
        'Works on any one of your barter campaigns',
        `Use it within ${validDays} days of buying`,
      ];

  return (
    <main className='addon-checkout'>
      <CheckoutLogo />
      <div className='addon-checkout__card'>
        {brandName && <span className='addon-checkout__brand'>For {brandName}</span>}
        <div className='addon-checkout__icon'>{isBarterCampaign ? <GiftIcon /> : <UsersIcon />}</div>
        <h1 className='addon-checkout__title'>{title}</h1>
        <p className='addon-checkout__body'>{body}</p>

        <div className='addon-checkout__price'>
          {plan ? (
            <>
              <div className='addon-checkout__amount'>{price}</div>
              <div className='addon-checkout__amount-note'>One-time payment · inclusive of all taxes</div>
            </>
          ) : (
            <>
              <div className='addon-checkout__skeleton' style={{ width: 120, height: 36 }} />
              <div className='addon-checkout__skeleton mb-0' style={{ width: 200, height: 12 }} />
            </>
          )}
        </div>

        <ul className='addon-checkout__list'>
          {benefits.map((b) => (
            <li key={b}><CheckIcon />{b}</li>
          ))}
        </ul>

        <button type='button' className='addon-checkout__pay' disabled={!plan || loading} onClick={onPay}>
          {loading ? 'Please wait…' : plan ? `Pay ${price}` : 'Loading…'}
        </button>
        <div className='addon-checkout__secure'><LockIcon />Secure payment by Razorpay</div>
      </div>

      <p className='addon-checkout__hint'>
        {isBarterCampaign
          ? 'After paying, go back to the ClanConnect app. Your brief goes for review.'
          : 'After paying, go back to the ClanConnect app to see your proposals.'}
      </p>
    </main>
  );
};
